import { toBcp47Locale } from '../locales/locale';

export type WritingStreak = {
  length: number;
  startDate: string;
  endDate: string;
};

export type WritingDaySeriesPoint = {
  writeDate: string;
  wordCount: number;
};

export type WritingDayGain = {
  writeDate: string;
  gain: number;
};

export type InitialImport = {
  writeDate: string;
  wordCount: number;
};

export type WritingGap = {
  length: number;
  gapStartDate: string;
  gapEndDate: string;
};

export type FavoriteWeekday = {
  weekday: number;
  count: number;
};

export type WordCountMilestone = {
  threshold: number;
  writeDate: string;
};

export const WORD_COUNT_MILESTONES = [1000, 5000, 10000, 25000, 50000, 100000] as const;

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export function parseUtcDateKeyToTimestamp(dateKey: string): number {
  const [year, month, day] = dateKey.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
}

export function addUtcDaysToDateKey(dateKey: string, days: number): string {
  const timestamp = parseUtcDateKeyToTimestamp(dateKey) + days * ONE_DAY_MS;
  return new Date(timestamp).toISOString().slice(0, 10);
}

export function buildDocumentWritingSeries(
  writingDays: ReadonlyArray<{ writeDate: string; documentWordCount: number }>,
  liveWordCount: number,
  todayKey: string
): WritingDaySeriesPoint[] {
  const byDate = new Map<string, number>();

  for (const day of writingDays) {
    byDate.set(day.writeDate, day.documentWordCount);
  }

  if (liveWordCount > 0 && byDate.has(todayKey)) {
    byDate.set(todayKey, Math.max(byDate.get(todayKey) ?? 0, liveWordCount));
  }

  return Array.from(byDate.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([writeDate, count]) => ({
      writeDate,
      wordCount: count,
    }));
}

export function collectDocumentWriteDates(
  writingDays: ReadonlyArray<{ writeDate: string }>
): string[] {
  return Array.from(new Set(writingDays.map((day) => day.writeDate))).sort();
}

export function computeDailyWordGains(series: readonly WritingDaySeriesPoint[]): WritingDayGain[] {
  return series.map((point, index) => ({
    writeDate: point.writeDate,
    gain: index === 0
      ? point.wordCount
      : Math.max(0, point.wordCount - series[index - 1].wordCount),
  }));
}

function median(values: readonly number[]): number {
  if (values.length === 0) {
    return 0;
  }

  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }

  return sorted[middle];
}

export function detectInitialImport(gains: readonly WritingDayGain[]): InitialImport | null {
  if (gains.length < 2) {
    return null;
  }

  const firstDay = gains[0];
  const restGains = gains.slice(1).map((day) => day.gain);

  if (restGains.every((gain) => gain === 0)) {
    return null;
  }

  const medianRest = median(restGains);
  const maxRest = Math.max(...restGains);
  const outlierThreshold = Math.max(3 * medianRest, 2 * maxRest, 500);

  if (firstDay.gain <= outlierThreshold) {
    return null;
  }

  return {
    writeDate: firstDay.writeDate,
    wordCount: firstDay.gain,
  };
}

export function gainsExcludingInitialImport(gains: readonly WritingDayGain[]): WritingDayGain[] {
  if (!detectInitialImport(gains)) {
    return [...gains];
  }

  return gains.slice(1);
}

export function findLongestWritingStreak(sortedDateKeys: readonly string[]): WritingStreak | null {
  if (sortedDateKeys.length === 0) {
    return null;
  }

  let best: WritingStreak = {
    length: 1,
    startDate: sortedDateKeys[0],
    endDate: sortedDateKeys[0],
  };

  let currentStart = sortedDateKeys[0];
  let currentEnd = sortedDateKeys[0];
  let currentLength = 1;

  for (let index = 1; index < sortedDateKeys.length; index += 1) {
    const dateKey = sortedDateKeys[index];
    const previousTimestamp = parseUtcDateKeyToTimestamp(currentEnd);
    const currentTimestamp = parseUtcDateKeyToTimestamp(dateKey);

    if (currentTimestamp - previousTimestamp === ONE_DAY_MS) {
      currentLength += 1;
      currentEnd = dateKey;
    } else {
      currentStart = dateKey;
      currentEnd = dateKey;
      currentLength = 1;
    }

    if (
      currentLength > best.length
      || (currentLength === best.length && currentEnd > best.endDate)
    ) {
      best = {
        length: currentLength,
        startDate: currentStart,
        endDate: currentEnd,
      };
    }
  }

  return best;
}

export function findCurrentWritingStreak(
  sortedDateKeys: readonly string[],
  todayKey: string
): WritingStreak | null {
  if (sortedDateKeys.length === 0 || !sortedDateKeys.includes(todayKey)) {
    return null;
  }

  let startDate = todayKey;
  let index = sortedDateKeys.indexOf(todayKey);

  while (index > 0) {
    const previousDate = sortedDateKeys[index - 1];
    const dayDifference =
      (parseUtcDateKeyToTimestamp(sortedDateKeys[index]) - parseUtcDateKeyToTimestamp(previousDate))
      / ONE_DAY_MS;

    if (dayDifference !== 1) {
      break;
    }

    startDate = previousDate;
    index -= 1;
  }

  const length = sortedDateKeys.indexOf(todayKey) - index + 1;

  return {
    length,
    startDate,
    endDate: todayKey,
  };
}

export function findBiggestWritingDay(gains: readonly WritingDayGain[]): WritingDayGain | null {
  if (gains.length === 0) {
    return null;
  }

  return gains.reduce((best, current) => (current.gain > best.gain ? current : best));
}

export function findWordsWrittenOnDate(
  gains: readonly WritingDayGain[],
  dateKey: string
): number {
  const day = gains.find((entry) => entry.writeDate === dateKey);
  return day?.gain ?? 0;
}

export function computeAverageWordsPerWritingDay(gains: readonly WritingDayGain[]): number | null {
  if (gains.length === 0) {
    return null;
  }

  const total = gains.reduce((sum, day) => sum + day.gain, 0);
  return Math.round(total / gains.length);
}

export function findLongestWritingGap(sortedDateKeys: readonly string[]): WritingGap | null {
  if (sortedDateKeys.length < 2) {
    return null;
  }

  let best: WritingGap | null = null;

  for (let index = 1; index < sortedDateKeys.length; index += 1) {
    const previousDate = sortedDateKeys[index - 1];
    const currentDate = sortedDateKeys[index];
    const gapLength =
      (parseUtcDateKeyToTimestamp(currentDate) - parseUtcDateKeyToTimestamp(previousDate))
      / ONE_DAY_MS
      - 1;

    if (gapLength <= 0) {
      continue;
    }

    if (!best || gapLength > best.length) {
      best = {
        length: gapLength,
        gapStartDate: addUtcDaysToDateKey(previousDate, 1),
        gapEndDate: addUtcDaysToDateKey(currentDate, -1),
      };
    }
  }

  return best;
}

export function findFavoriteWeekday(sortedDateKeys: readonly string[]): FavoriteWeekday | null {
  if (sortedDateKeys.length === 0) {
    return null;
  }

  const counts = new Map<number, number>();

  for (const dateKey of sortedDateKeys) {
    const weekday = new Date(parseUtcDateKeyToTimestamp(dateKey)).getUTCDay();
    counts.set(weekday, (counts.get(weekday) ?? 0) + 1);
  }

  let favorite: FavoriteWeekday | null = null;

  for (const [weekday, count] of counts.entries()) {
    if (!favorite || count > favorite.count || (count === favorite.count && weekday > favorite.weekday)) {
      favorite = { weekday, count };
    }
  }

  return favorite;
}

export function findWordCountMilestones(series: readonly WritingDaySeriesPoint[]): WordCountMilestone[] {
  const milestones: WordCountMilestone[] = [];
  let nextThresholdIndex = 0;

  for (const point of series) {
    while (
      nextThresholdIndex < WORD_COUNT_MILESTONES.length
      && point.wordCount >= WORD_COUNT_MILESTONES[nextThresholdIndex]
    ) {
      milestones.push({
        threshold: WORD_COUNT_MILESTONES[nextThresholdIndex],
        writeDate: point.writeDate,
      });
      nextThresholdIndex += 1;
    }
  }

  const highestPerDay = new Map<string, WordCountMilestone>();

  for (const milestone of milestones) {
    const existing = highestPerDay.get(milestone.writeDate);
    if (!existing || milestone.threshold > existing.threshold) {
      highestPerDay.set(milestone.writeDate, milestone);
    }
  }

  return Array.from(highestPerDay.values()).sort((left, right) => {
    if (left.writeDate !== right.writeDate) {
      return left.writeDate.localeCompare(right.writeDate);
    }
    return left.threshold - right.threshold;
  });
}

export function formatWritingStreakRange(
  streak: WritingStreak,
  locale: string,
  formatDate: (dateKey: string, locale: string) => string
): string {
  if (streak.length === 1) {
    return formatDate(streak.startDate, locale);
  }
  return `${formatDate(streak.startDate, locale)} – ${formatDate(streak.endDate, locale)}`;
}

export function formatWritingGapRange(
  gap: WritingGap,
  locale: string,
  formatDate: (dateKey: string, locale: string) => string
): string {
  if (gap.length === 1) {
    return formatDate(gap.gapStartDate, locale);
  }
  return `${formatDate(gap.gapStartDate, locale)} – ${formatDate(gap.gapEndDate, locale)}`;
}

export function formatUtcWeekdayLabel(weekday: number, locale: string): string {
  const referenceDate = new Date(Date.UTC(2024, 0, 7 + weekday));
  return new Intl.DateTimeFormat(toBcp47Locale(locale), {
    weekday: 'long',
    timeZone: 'UTC',
  }).format(referenceDate);
}
